import { splitContextUrls } from '@reflex/ui/client/context-url-matchers';

/**
 * A URL that a context matcher claims in full — the plugin can turn it into a
 * reference chip on its own. Anything a matcher only partly covers (a PR link
 * buried in a redirect's query string) or does not claim at all is left alone:
 * a link whose text we rewrite has to be worth a chip, or the rewrite is just
 * vandalism of the user's paste.
 */
function isContextUrl(url: string): boolean {
  const segments = splitContextUrls(url);
  return segments.length === 1 && segments[0].type === 'context';
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Find `label` in `text` at or after `from`, tolerating the whitespace the two
 * clipboard flavors disagree on: a chip's HTML can wrap or indent between the
 * spans that make up its label, where the plain text has a single space.
 * Matching word-by-word with `\s+` between the words keeps the two aligned
 * without either side being normalized (which would shift every later index).
 */
function findLabel(text: string, label: string, from: number): { at: number; end: number } | null {
  const words = label.split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const match = new RegExp(words.map(escapeRegex).join('\\s+')).exec(text.slice(from));
  return match ? { at: from + match.index, end: from + match.index + match[0].length } : null;
}

/** The pasted HTML, flattened to the order its text and its links appear in. */
type HtmlPiece = { type: 'text'; value: string } | { type: 'link'; href: string; label: string };

function htmlPieces(root: ParentNode): HtmlPiece[] {
  const pieces: HtmlPiece[] = [];
  for (const child of root.childNodes) {
    if (child.nodeType === child.TEXT_NODE) {
      pieces.push({ type: 'text', value: child.nodeValue ?? '' });
    } else if (child.nodeName === 'A' && child instanceof Element && child.hasAttribute('href')) {
      // The parser un-nests anchors, so a link never contains another one.
      pieces.push({
        type: 'link',
        href: child.getAttribute('href')?.trim() ?? '',
        label: child.textContent ?? '',
      });
    } else if (child.nodeType === child.ELEMENT_NODE) {
      pieces.push(...htmlPieces(child as Element));
    }
  }
  return pieces;
}

/**
 * Rewrite the labels of linked context items in a paste back to their URLs.
 *
 * The transcript renders a pull request as a chip — an `<a>` whose visible
 * text is `#4032`, not the URL. Copying that chip puts `#4032` on the
 * clipboard as `text/plain` and the anchor (href included) as `text/html`, so
 * a composer that only reads plain text sees a bare `#4032` and has nothing to
 * make a reference out of. The URL was on the clipboard the whole time; this
 * puts it back into the text, so copying a PR out of chat lands in the
 * composer as the same chip as copying its URL out of GitHub.
 *
 * Only anchors a context matcher claims are rewritten, and each rewrites the
 * one occurrence of its own label. Which occurrence that is comes from walking
 * the two flavors forward together — the prose between links advances the
 * cursor just as the links do — so a label that is also an ordinary word
 * earlier in the paste ("this", "here") isn't the one that gets replaced, and
 * two links to the same item both resolve. Prose itself is never rewritten.
 */
export function resolveLinkedContextUrls(text: string, html: string): string {
  if (!html) return text;
  let resolved = text;
  let cursor = 0;
  for (const piece of htmlPieces(new DOMParser().parseFromString(html, 'text/html').body)) {
    if (piece.type === 'text') {
      // Prose only moves the cursor. When the two flavors disagree (a plugin
      // rendered something the plain text spells differently) the cursor stays
      // put, and the next label is matched from wherever it last landed.
      const seen = findLabel(resolved, piece.value, cursor);
      if (seen) cursor = seen.end;
      continue;
    }
    const found = findLabel(resolved, piece.label, cursor);
    if (!found) continue;
    // A link nothing claims is prose too: it still moves the cursor.
    if (!piece.href || !isContextUrl(piece.href)) {
      cursor = found.end;
      continue;
    }
    resolved = resolved.slice(0, found.at) + piece.href + resolved.slice(found.end);
    cursor = found.at + piece.href.length;
  }
  return resolved;
}
